import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import Box from '@material-ui/core/Box';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { TableCell, TableRow } from '@this/src/components/shared/ui/data_displays/table';
import type TicketingInstruction from '@this/src/domain/arrangement/ticketing_instruction';
import { PreferredTicketingMethodLabel } from '@this/src/domain/arrangement/preferred_ticketing_method_query';
import type Supplier from '@this/src/domain/supplier/supplier';
import type SuppliedItem from '@this/src/domain/supplied_item/supplied_item';
import type PaymentMethodList from '@this/src/domain/payment_method/payment_method_list';
import type IndividualRailwaySuppliedItem from '@this/domain/individual_railway_supplied_item/individual_railway_supplied_item';
import LogModal from '../log_modal';
import TicketingInstructionActions from './ticketing_instruction_actions';

interface Props {
  ticketingInstruction: TicketingInstruction;
  suppliers: Supplier[];
  suppliedItems: SuppliedItem[];
  paymentMethods: PaymentMethodList | null;
  individualRailwaySuppliedItems: IndividualRailwaySuppliedItem[];
  optionReady: boolean;
  onChange: (ticketingInstruction: TicketingInstruction) => void;
}

const TicketingInstructionRow: React.FC<Props> = ({
  ticketingInstruction: ti,
  suppliers,
  suppliedItems,
  individualRailwaySuppliedItems,
  paymentMethods,
  onChange
}) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [showLogModal, setShowLogModal] = useState<boolean>(false);

  const handleClick = useCallback(async () => {
    await onChange(ti);
    setIsCompleted(true);
  }, [ti, onChange]);

  return (
    <TableRow key={ti.orderItemId} disabled={isCompleted || !!ti.snoozeTo}>
      <TableCell nowrap>{ti.startDate?.format('YYYY-MM-DD') || ''}</TableCell>
      <TableCell nowrap>{ti.receivedAt.format('YYYY-MM-DD HH:mm')}</TableCell>
      <TableCell>{ti.organizationName}</TableCell>
      <TableCell>
        {ti.preferredTicketingMethod
          ? PreferredTicketingMethodLabel[ti.preferredTicketingMethod]
          : PreferredTicketingMethodLabel.unset}
      </TableCell>
      <TableCell>{ti.premiumSupport ? '◯' : ''}</TableCell>
      <TableCell>
        {ti.latestMessage?.split('\n').map((line, index) => (
          <Box key={index}>{line}</Box>
        ))}
      </TableCell>
      <TableCell nowrap>
        <Link href={`/arrangement/virtual_counter?trip_id=${ti.tripId}`} target="_blank" rel="noopener noreffer">
          {ti.tripId}
        </Link>
      </TableCell>
      <TableCell nowrap>{ti.traceId}</TableCell>
      <TableCell>
        {ti.latestLog && <span>{ti.latestLog}</span>}
        <p>
          (<Link onClick={() => setShowLogModal(true)}>ログ</Link>)
        </p>
      </TableCell>
      <TableCell style={{ whiteSpace: 'nowrap' }}>
        {ti.content?.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {isConvertWordToBold(line) ? <strong>{line}</strong> : <>{line}</>}
            {index !== (ti.content?.split('\n').length || 0) - 1 && <br />}
          </React.Fragment>
        ))}
      </TableCell>
      <TableCell style={{ whiteSpace: 'nowrap' }}>
        {ti.pickupStationInfo ? (
          <>
            <Box>{ti.pickupStationInfo.pickupStation || 'その他'}</Box>
            {ti.pickupStationInfo.prefectureName && <Box>{ti.pickupStationInfo.prefectureName}</Box>}
            {ti.pickupStationInfo.railwayAreaName && <Box>{ti.pickupStationInfo.railwayAreaName}</Box>}
            {ti.pickupStationInfo.railwayAreaTicketingService !== null &&
              ti.pickupStationInfo.railwayAreaTicketingService !== undefined && (
                <Box>{ti.pickupStationInfo.railwayAreaTicketingService === 0 ? 'えきねっと' : 'e5489'}</Box>
              )}
            {ti.pickupStationInfo.pickupStation === 'その他' && ti.pickupStationInfo.pickupService !== null && (
              <Box>ユーザー選択: {ti.pickupStationInfo.pickupService === 0 ? 'えきねっと' : 'e5489'}</Box>
            )}
          </>
        ) : (
          '-'
        )}
      </TableCell>
      {/* <TableCell>鉄道会社</TableCell> */}
      <TicketingInstructionActions
        ticketingInstruction={ti}
        suppliers={suppliers}
        suppliedItems={suppliedItems}
        paymentMethods={paymentMethods}
        individualRailwaySuppliedItems={individualRailwaySuppliedItems}
        onChange={handleClick}
        isCompleted={isCompleted}
      />
      {showLogModal && ti.traceId && <LogModal traceId={ti.traceId} onClose={() => setShowLogModal(false)} />}
    </TableRow>
  );
};

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.ticketingInstruction === nextProps.ticketingInstruction &&
    prevProps.optionReady === nextProps.optionReady
  );
};

const isConvertWordToBold = (message: string): boolean => {
  return message.startsWith('座席：') && message.startsWith('座席：指定席') === false;
};

export default React.memo(observer(TicketingInstructionRow), propsAreEqual);
